import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageLayout from "../../components/layout/PageLayout";
import LicitacionMenu from "./LicitacionMenu";
import TenderData from "../../Data/TenderData";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { X, FileText, Paperclip } from "lucide-react";
import { toast } from "sonner";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // Tamaño máximo de archivo en bytes (10 MB)

const CreateTenderStep2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialData = location.state?.formData || {}; // Revisar si location.state incluye formData completo
  const [formData, setFormData] = useState({
    titulo: initialData.titulo || "",
    descripcion: initialData.descripcion || "",
    archivosAdjuntos: initialData.archivosAdjuntos || [],
    categoria: initialData.categoria || "",
    ...initialData, // Mantener datos adicionales al pasar de un paso a otro
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const ALLOWED_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "pdf",
    "docx",
    "doc",
    "xlsx",
    "xls",
    "csv",
    "txt",
    "pptx",
    "ppt",
    "zip",
    "rar",
    "7z",
  ];

  // ✅ Guardar archivos seleccionados en el estado (sin subir aún)
  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    const validFilesBySize = files.filter((file) => file.size <= MAX_FILE_SIZE);

    // ✅ Detectar archivos con formatos no permitidos antes de filtrarlos
    const invalidFiles = files.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      toast.error(
        `Algunos archivos tienen formatos no permitidos y no se han añadido:\n\n${invalidFiles
          .map((file) => `- ${file.name}`)
          .join("\n")}\n\nFormatos permitidos: ${ALLOWED_EXTENSIONS.join(", ")}`
      );
    }

    // ✅ Filtrar archivos válidos por extensión después de mostrar la alerta
    const validFilesByExtension = validFilesBySize.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return ALLOWED_EXTENSIONS.includes(fileExtension);
    });

    setFormData({
      ...formData,
      archivosAdjuntos: [
        ...(formData.archivosAdjuntos || []),
        ...validFilesByExtension.map((file) => ({
          file, // ✅ Guarda el archivo en sí
          name: file.name, // ✅ Guarda el nombre del archivo
        })),
      ].slice(0, 3),
    });
  };

  // ✅ Eliminar archivo antes de enviarlo en Preview
  const handleFileRemove = (indexToRemove) => {
    setFormData({
      ...formData,
      archivosAdjuntos: formData.archivosAdjuntos.filter(
        (_, index) => index !== indexToRemove
      ),
    });
  };

  // ✅ Modificar handleNext para solo pasar la info sin subir archivos
  const handleNext = (e) => {
    e.preventDefault();
    navigate("/create-tender-step-3", { state: { formData } });
  };

  const handleBack = () => {
    navigate("/create-tender-step-1", { state: { formData } });
  };

  // ✅ Obtener datos de ejemplo según el tipo de servicio seleccionado
  const exampleData = TenderData[formData.categoria] || {};

  return (
    <PageLayout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <LicitacionMenu currentStep={2} />

        <h3 className="text-xl font-semibold text-foreground">Crear Licitación - Paso 2</h3>
        <form onSubmit={handleNext} className="mt-6 flex flex-col gap-6">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Título del proyecto:</label>
            <Input
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleInputChange}
              placeholder={`Ejemplo: ${exampleData.tituloEjemplo || ""}`}
              minLength={5}
              maxLength={200}
              required
            />
            <small className="text-xs text-muted-foreground">{`${formData.titulo.length}/200 caracteres`}</small>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Descripción:</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              placeholder={`Ejemplo: ${exampleData.descripcionEjemplo || ""}`}
              minLength={30}
              maxLength={3000}
              required
              rows={6}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            <small className="text-xs text-muted-foreground">{`${formData.descripcion.length}/3000 caracteres`}</small>
          </div>

          {/* ✅ Sección de subida de archivos */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Archivos adjuntos (máx. 3 archivos, 10 MB c/u):
            </label>
            <label className="flex w-fit cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
              <input
                type="file"
                onChange={handleFileSelection}
                multiple
                accept=".jpg,.jpeg,.png,.pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.pptx,.ppt,.zip,.rar,.7z"
                className="hidden"
              />
              <Paperclip className="h-4 w-4" /> Seleccionar archivos
            </label>

            <small className="mt-1 block text-xs text-muted-foreground">
              Formatos permitidos: jpg, jpeg, png, pdf, docx, doc, xlsx, xls, csv, txt, pptx, ppt, zip, rar, 7z
            </small>

            <div className="mt-2 space-y-1">
              {formData?.archivosAdjuntos?.map((file, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <button
                    type="button"
                    onClick={() => handleFileRemove(index)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <a
                    href={URL.createObjectURL(file.file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" /> {file.name}
                  </a>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Retroceder
            </Button>
            <Button type="submit" disabled={!formData.titulo || !formData.descripcion}>
              Siguiente
            </Button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

export default CreateTenderStep2;
